import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatDropdown } from "blocks-nextjs/blocks-react/components/Dropdown/Dropdown";
import { CatInputField } from "blocks-nextjs/blocks-react/components/InputField/InputField";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatModal } from "blocks-nextjs/blocks-react/components/Modal/Modal";
import { CatTextPassage } from "blocks-nextjs/blocks-react/components/TextPassage/TextPassage";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
const AssetDetailsModal = (props) => {
    const { defaultAssetDetails, modalVariant = "ThirdPartyAssetDetails", onApplyChanges, onCloseModal, showModal } = props;
    const isEditAssetNameVariant = modalVariant === "isEditAssetName";
    const isUnknownAssetVariant = modalVariant === "isUnknownAsset";
    const AssetCategoryOptions = {
        Charger: "CHARGER",
        "Charging Unit / ESS": "ESS",
        "Production Machine": "MACHINE"
    };
    const ThirdPartyAssetDetailsSchema = z.object({
        assetMetadata: z.object({
            productFamily: z.object({ code: z.string().trim().min(1, "product family code is required") })
        }),
        assetName: z.string().trim().min(1, "Asset name is required"),
        assetSerialNumber: z.string().trim().min(1, "Serial number is required"),
        assetSourceId: z.string().trim().min(1, "Source id is required"),
        assetSourceType: z.string().trim().min(1, "Select any one option")
    });
    const EditAssetNameSchema = z.object({
        assetId: z.string().trim().min(1, "Asset name is required"),
        assetName: z.string().trim().min(1, "Asset name is required")
    });
    const UnknownAssetDetailsSchema = z.object({
        assetId: z.string().trim().min(1, "Asset ID is required"),
        assetName: z.string().trim().min(1, "Machine name is required"),
        assetSerialNumber: z.string().trim().min(1, "Serial number is required")
    });
    const variantConfig = {
        ThirdPartyAssetDetails: {
            assetNameLabel: "Asset Name",
            assetNamePlaceholder: "Enter Asset Name",
            heading: "Add Asset",
            schema: ThirdPartyAssetDetailsSchema,
            sourceTypeLabel: "Asset source type",
            sourceTypeOptions: ["Machine", "ESS"],
            subTitle: "Input the non-CAT OEM details to add to the worksite calculation.",
            submitLabel: "Apply"
        },
        isEditAssetName: {
            assetNameLabel: "Asset Name",
            assetNamePlaceholder: "Enter Asset Name",
            heading: "Edit Asset Name",
            schema: EditAssetNameSchema,
            sourceTypeLabel: "Asset source type",
            sourceTypeOptions: ["Machine", "ESS"],
            subTitle: "Update Asset Name",
            submitLabel: "Apply"
        },
        isUnknownAsset: {
            assetNameLabel: "Machine name",
            assetNamePlaceholder: "Enter machine name",
            heading: "Edit Asset Details",
            schema: UnknownAssetDetailsSchema,
            sourceTypeLabel: "Asset Category",
            sourceTypeOptions: ["Production Machine", "Charging Unit / ESS", "Charger"],
            subTitle: "Update the asset’s name and serial number below. Changes will be saved once confirmed.",
            submitLabel: "Save Changes"
        }
    };
    const { assetNameLabel, assetNamePlaceholder, heading, schema: ValidationSchema, sourceTypeLabel, sourceTypeOptions, subTitle, submitLabel } = variantConfig[modalVariant];
    const showSerialAndCategoryFields = !isEditAssetNameVariant;
    const { control, formState: { errors, isDirty, isValid }, getValues, reset, trigger } = useForm({
        defaultValues: defaultAssetDetails,
        mode: "onBlur",
        resolver: zodResolver(ValidationSchema)
    });
    const inputFieldConfigs = [
        {
            dataTest: "asset-name",
            fieldId: "text-field-11",
            label: assetNameLabel,
            name: "assetName",
            placeholder: assetNamePlaceholder
        },
        ...(showSerialAndCategoryFields
            ? [
                {
                    dataTest: "asset-serial-number",
                    fieldId: "text-field-1",
                    label: "Serial#",
                    name: "assetSerialNumber",
                    placeholder: "Enter Serial Number"
                }
            ]
            : [])
    ];
    useEffect(() => {
        reset(defaultAssetDetails);
    }, [defaultAssetDetails, reset, showModal]);
    const buildFormPayload = () => {
        const getStringValue = (field) => getValues(field) ?? "";
        if (isEditAssetNameVariant) {
            return { assetDetails: [{ assetId: getStringValue("assetId"), assetName: getStringValue("assetName") }] };
        }
        if (isUnknownAssetVariant) {
            const categoryLabel = getStringValue("assetCategory");
            return {
                assetCategory: AssetCategoryOptions[categoryLabel] || categoryLabel,
                assetId: getStringValue("assetId"),
                assetName: getStringValue("assetName"),
                assetSerialNumber: getStringValue("assetSerialNumber")
            };
        }
        return {
            assetMetadata: getValues("assetMetadata"),
            assetName: getStringValue("assetName"),
            assetSerialNumber: getStringValue("assetSerialNumber"),
            assetSourceId: getStringValue("assetSourceId"),
            assetSourceType: getStringValue("assetSourceType")
        };
    };
    return (_jsx("form", { children: _jsxs(CatModal, { "data-test": "add-asset-modal", disableBackdropClick: true, heading: heading, headingVariant: "title", isActive: showModal, onBlModalClose: () => {
                onCloseModal();
            }, size: "sm", children: [_jsxs("div", { className: "flex flex-col gap-5", children: [_jsx(CatTextPassage, { size: "sm", children: _jsx("p", { children: subTitle }) }), inputFieldConfigs.map(({ dataTest, fieldId, label, name, placeholder }) => (_jsx(Controller, { control: control, defaultValue: "", name: name, render: ({ field: { onBlur, onChange, ref, value } }) => (_jsx(CatInputField, { ariaDescribedBy: "text-field-aria-1", "data-test": dataTest, fieldId: fieldId, label: label, onBlChange: onChange, onBlur: onBlur, placeholder: placeholder, ref: ref, required: true, size: "lg", value: value, ...(name in errors &&
                                    errors[name] && {
                                    errorNote: errors[name]?.message,
                                    status: "error"
                                }) })) }, name))), showSerialAndCategoryFields && !isUnknownAssetVariant && (_jsx(Controller, { control: control, defaultValue: "", name: isUnknownAssetVariant ? "assetCategory" : "assetSourceType", render: ({ field: { onChange, ref, value } }) => (_jsx(CatDropdown, { ariaDescribedBy: "dropdown-field-aria-1", "data-test": "asset-source-type", disabled: isEditAssetNameVariant, fieldId: "dropdown-1", label: sourceTypeLabel, onBlDropdownClose: () => trigger(isUnknownAssetVariant ? "assetCategory" : "assetSourceType"), onBlSelect: (e) => {
                                    onChange(e);
                                    trigger(isUnknownAssetVariant ? "assetCategory" : "assetSourceType");
                                }, placeholder: "Select an option...", ref: ref, required: true, value: value, ...((isUnknownAssetVariant ? "assetCategory" : "assetSourceType") in errors &&
                                    (isUnknownAssetVariant
                                        ? errors?.assetCategory?.message
                                        : errors?.assetSourceType?.message) && {
                                    errorNote: isUnknownAssetVariant
                                        ? errors.assetCategory?.message
                                        : errors.assetSourceType?.message,
                                    status: "error"
                                }), children: _jsx(React.Fragment, { children: _jsx(CatList, { spacing: "padded", children: sourceTypeOptions.map((item) => (_jsx(CatListItem, { value: item, children: item }, item))) }) }, ".0") })) }))] }), _jsxs("div", { className: "flex gap-3", slot: "footer", children: [_jsx(CatButton, { disabled: !isDirty || !isValid, onClick: () => {
                                onApplyChanges(buildFormPayload());
                                reset();
                            }, variant: "primary", children: submitLabel }), _jsx(CatButton, { "data-test": "cancel-button", onClick: () => {
                                onCloseModal();
                            }, variant: "ghost", children: "Cancel" })] })] }) }));
};
export default AssetDetailsModal;
